import { useWindowVirtualizer } from "@tanstack/react-virtual";
import moment from "moment/min/moment-with-locales";
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Col, Form, Stack } from "react-bootstrap";
import { Await, useRouteLoaderData } from "react-router-dom";
import { axiosInstance } from "../auth/Auth";
import DataDescription from "../components/DataDescription";
import EmptyRow from "../components/EmptyRow";
import { findRadioDayparts, formatDaypartText, getRadioName } from "../components/HelperFunctions";
import { useFetchData, useResolvedRadioNames } from "../components/HelperHooks";
import PageDesciption from "../components/PageDesciption";
import DatesPicker, { latestFull2Weeks, latestFullMonth, latestFullQuarter, latestFullWeek } from "../components/Pickers/DatesPicker";
import DemoPicker from "../components/Pickers/DemoPicker";
import MinMaxSpinsPicker from "../components/Pickers/MinMaxSpinsPicker";
import MusicTestDaypartPicker from "../components/Pickers/MusicTestDaypartPicker";
import StationPicker from "../components/Pickers/StationPicker";
import SearchInput from "../components/SearchInput";
import { useMusicTestTrendModal } from "../components/TrendOverTime";
import { useUserFormState } from "../components/UserSelection";
import MusicTestHeader from "../components/musicTest/MusicTestHeader";
import MusicTestRow from "../components/musicTest/MusicTestRow";
import { DataContent, DescriptionWithDownloadRow, InputSelection, ModulePage, VirtualizedWindowContainer } from "../components/pageSections";
import { useSongCatalogueModal } from "../components/songCatalogue/SongCatalogueModal";
import useSortableData from "../components/useSortableData";

export default function MusicTesting() {
    const moduleName = "musicTesting";
    const { musicTestDayparts, radiosModuleSettings } = useRouteLoaderData("root");

    const [formData, setFormData, saveFormData, loadFormData] = useUserFormState(moduleName);
    const [loading, errorCode, data, fetchData] = useFetchData();

    const [filterData, setFilterData] = useState({ hideDeltas: true, hideInvalid: true, minSpins: 0, maxSpins: 0, searchString: "" });

    const [latestCalculatedDates, setLatestCalculatedDates] = useState([]);
    const [latestCalculatedDate, setLatestCalculatedDate] = useState(null);
    const [burnLocked, setBurnLocked] = useState(false);
    const [showDeltaSpins, setShowDeltaSpins] = useState(false);
    const [resultCount, setResultCount] = useState(0);

    const { setCatalogueSong, SongCatalogueModalComponent } = useSongCatalogueModal();
    const { setTrendSong, MusicTestTrendModalComponent } = useMusicTestTrendModal();
    const resolvedRadioNames = useResolvedRadioNames();

    const tableContainerRef = useRef(null);

    const isCombined = data?.radio_ids.length > 1;

    const dataMemo = useMemo(() => {
        if (data) {
            let filteredData = data.data;
            // probably temporary. Adds individual data object for non existant indiviudal data so path to it is consistent and sorting using path to property is possible
            if (data.radio_ids.length > 1) {
                for (const s of filteredData) {
                    s.individual = data.radio_ids.map(r => s.individual.find(x => x.radio_id === r) ?? { radio_id: r });
                }
            }
            if (filterData.minSpins > 0) {
                filteredData = filteredData.filter(x => x.combined.spins >= filterData.minSpins);
            }
            if (filterData.maxSpins > 0) {
                filteredData = filteredData.filter(x => x.combined.spins <= filterData.maxSpins);
            }
            if (filterData.hideInvalid) {
                filteredData = filteredData.filter(x => x.combined.valid);
            }
            if (filterData.searchString) {
                filteredData = filteredData.filter(
                    x =>
                        x.song.artist.toLowerCase().includes(filterData.searchString.toLowerCase().trim()) ||
                        x.song.title.toLowerCase().includes(filterData.searchString.toLowerCase().trim()) ||
                        x.song.release_year?.toString().includes(filterData.searchString.trim())
                );
            }
            return filteredData;
        }
    }, [filterData.hideInvalid, filterData.maxSpins, filterData.minSpins, data, filterData.searchString]);

    const predefinedRanges = useMemo(() => {
        if (latestCalculatedDates?.length && formData.daypartId !== "placeholder") {
            const baseDate = moment(latestCalculatedDates.find(x => x.daypart_id === formData.daypartId)?.latest_date).endOf("isoWeek");
            return [latestFullWeek(baseDate), latestFull2Weeks(baseDate), latestFullMonth(baseDate), latestFullQuarter(baseDate)];
        }
    }, [latestCalculatedDates, formData.daypartId]);

    const { sortedItems, requestSort, sortConfig } = useSortableData(dataMemo, { key: "combined.spins", direction: "descending" });

    useEffect(() => {
        setResultCount(sortedItems?.length ?? 0);
    }, [sortedItems]);

    useEffect(() => {
        async function getLatestCalculatedDates(radioId, dataType) {
            const response = await axiosInstance.get(`music_test/${radioId}/latest_daypart_dates/?data_type=${dataType}`);
            setLatestCalculatedDates(response.data);
        }
        if (formData.station[0]?.radio_id && formData.demo[0]?.data_type) {
            getLatestCalculatedDates(formData.station[0].radio_id, formData.demo[0].data_type);
        }
    }, [formData.demo, formData.station]);

    useEffect(() => {
        if (latestCalculatedDates.length && formData.daypartId !== "placeholder") {
            const latestDate = moment(latestCalculatedDates.find(x => x.daypart_id === formData.daypartId)?.latest_date).endOf("isoWeek");
            setLatestCalculatedDate(latestDate);
            if (!formData.dateRange || moment(formData.dateRange[1]) > latestDate) {
                setFormData(current => ({ ...current, dateRange: latestFullWeek(latestDate, true) }));
            }
        }
    }, [formData.dateRange, formData.daypartId, latestCalculatedDates, setFormData]);

    const csvHeaders = useMemo(
        () =>
            isCombined && resolvedRadioNames
                ? [
                      ...["Item ID", "Artist", "Title", "Release Year", "Combined Spins", "Combined Delta Spins", "Combined Score", "Combined Burn"],
                      ...data?.radio_ids.map(r => `${getRadioName(r, resolvedRadioNames)} Score`),
                  ]
                : ["Item ID", "Artist", "Title", "Release Year", "Spins", "Delta Spins", "Score", "Burn", "Delta %", "Familiarity", "Impressions"],
        [isCombined, data?.radio_ids, resolvedRadioNames]
    );
    const csvData = useMemo(
        () =>
            isCombined
                ? sortedItems?.map(x => [
                      ...[
                          x.song.item_id,
                          x.song.artist,
                          x.song.title,
                          x.song.release_year,
                          x.combined.spins,
                          x.combined.spins_delta,
                          x.combined.song_score?.toFixed(1),
                          x.combined.burn?.toFixed(1),
                      ],
                      ...data?.radio_ids.map(r => x.individual.find(i => i.radio_id === r)?.song_score?.toFixed(1)),
                  ])
                : sortedItems?.map(x => [
                      x.song.item_id,
                      x.song.artist,
                      x.song.title,
                      x.song.release_year,
                      x.combined.spins,
                      x.combined.spins_delta,
                      x.combined.song_score?.toFixed(1),
                      x.combined.burn?.toFixed(1),
                      x.combined.weighted_delta?.toFixed(3),
                      x.combined.familiarity_tier,
                      x.combined.impressions,
                  ]),
        [isCombined, data?.radio_ids, sortedItems]
    );

    async function handleFormSubmit(event) {
        event.preventDefault();
        await saveFormData();
        await saveFormData(formData.station[0].radio_id);
        const input = {
            radio_ids: formData.station.map(x => x.radio_id),
            start_date: moment(formData.dateRange[0]).format("YYYY-MM-DD"),
            end_date: moment(formData.dateRange[1]).format("YYYY-MM-DD"),
            daypart_id: formData.daypartId,
            demo_id: formData.demo[0].demo_id,
        };
        await fetchData("music_test/", input);
        const station = formData.station[0];

        const resolvedModuleSettings = await radiosModuleSettings;
        const moduleSetting = resolvedModuleSettings.data.find(m => m.radio_id === station.radio_id && m.module_id === 14) ? false : true;

        setBurnLocked(moduleSetting);
        setShowDeltaSpins(formData.demo[0].data_type === 2);
    }

    const virtualizer = useWindowVirtualizer({
        count: sortedItems?.length || 1,
        estimateSize: useCallback(() => 41, []),
        getItemKey: useCallback(i => sortedItems?.length && sortedItems[i].song.item_id, [sortedItems]),
        overscan: 20,
        scrollMargin: tableContainerRef.current?.offsetTop ?? 0,
    });

    return (
        <ModulePage>
            <ModulePage.Title>Music Testing</ModulePage.Title>
            <ModulePage.InputSelection
                handleFormSubmit={handleFormSubmit}
                disabled={!formData.station.length || !formData.demo.length || formData.daypartId === "placeholder" || !formData.dateRange}>
                <InputSelection.Main>
                    <StationPicker
                        formData={formData}
                        setFormData={setFormData}
                        name={moduleName}
                        moduleFilterFunction={x => x.module_id === 9}
                        filterFunction={x => !x.mtt_only}
                        multiple
                        label="Station(s)"
                        loadOnChange={loadFormData}
                    />
                    <Suspense>
                        <Await resolve={radiosModuleSettings}>
                            {resolvedModuleSettings => (
                                <DemoPicker
                                    formData={formData}
                                    setFormData={setFormData}
                                    name={moduleName}
                                    disabled={!formData.station.length}
                                    filterFunction={x => {
                                        if (!formData.station.length) return false;
                                        const station = formData.station[0];
                                        const hasListenerGroupModule = resolvedModuleSettings.data.find(
                                            r => r.radio_id === station.radio_id && r.module_id === 7 && r.enabled
                                        );
                                        return (
                                            x.music_test &&
                                            ((x.data_type === 1 && station.stream && (hasListenerGroupModule ? true : x.listener_group === 0)) ||
                                                (x.data_type === 2 && station.ppm))
                                        );
                                    }}
                                />
                            )}
                        </Await>
                    </Suspense>
                    <MusicTestDaypartPicker formData={formData} setFormData={setFormData} name={moduleName} />
                    <DatesPicker
                        formData={formData}
                        setFormData={setFormData}
                        name={moduleName}
                        predefinedRanges={predefinedRanges}
                        latestAvailableDate={latestCalculatedDate}
                        hoverRange={"week"}
                        disabled={formData.daypartId === "placeholder"}
                    />
                </InputSelection.Main>
            </ModulePage.InputSelection>
            <ModulePage.DataContent loading={loading} errorCode={errorCode}>
                <DataContent.Placeholder show={!data}>
                    <PageDesciption
                        title={"Music Test"}
                        whatList={[
                            "Test every song you play, every week",
                            "Gauge listener reactions to the music you play, and see trends on each song for listener appea",
                            "Combine multiple stations you operate to create a 'format' test or mix audience types",
                        ]}
                        knowList={[
                            { text: "Song Score basics: if its green play it more, if its red play it less" },
                            {
                                text: "Check out our ",
                                url: "https://radioanalyzer.com/pdf/Music_Score_and_Burn.pdf",
                                title: "Song Score and Burn",
                                text2: " for a more detailed explanation of what the numbers mean, or just give us a call",
                            },
                            { text: "Data is collected weekly, but you can also display the trend month by month" },
                        ]}
                        relatedLinks={[
                            {
                                title: "Christmas Music – What to Play, and When",
                                url: "https://radioanalyzer.com/blog/christmas-music-what-to-play-and-when",
                            },
                            {
                                title: "Radio Mythbusting – Do Ballads in the Morning Show Really Hurt Your Listening Curve?",
                                url: "https://radioanalyzer.com/blog/radio-mythbusting-do-ballads-in-the-morning-show-really-hurt-your-listening-curve",
                            },
                        ]}
                        videoSrc="https://www.youtube.com/embed/fJph2n7L1lg?si=FHktMcwP55sE0-1a"
                    />
                </DataContent.Placeholder>
                {data && (
                    <>
                        <DescriptionWithDownloadRow csvHeaders={csvHeaders} data={csvData} filename="Music Testing">
                            <DataDescription>
                                <DataDescription.Stations radioIds={data?.radio_ids} />
                                <DataDescription.DateRange startDate={data?.start_date} endDate={data?.end_date} />
                                <DataDescription.Simple label="Perspective">
                                    <Suspense>
                                        <Await resolve={musicTestDayparts}>
                                            {resolvedDayparts => (
                                                <span className="fw-bold">
                                                    {formatDaypartText(
                                                        findRadioDayparts(resolvedDayparts.data, data.radio_ids[0]).find(x => x.daypart_id === data.daypart_id)
                                                    )}
                                                </span>
                                            )}
                                        </Await>
                                    </Suspense>
                                </DataDescription.Simple>
                                <DataDescription.Demo demoId={data?.demo_id} />
                                {(filterData.minSpins > 0 || filterData.maxSpins > 0) && (
                                    <DataDescription.Simple label="Spins" value={`${filterData.minSpins} - ${filterData.maxSpins || "∞"}`} />
                                )}
                                <DataDescription.Simple label={"Results"} value={resultCount} />
                            </DataDescription>
                        </DescriptionWithDownloadRow>
                        <Stack direction="horizontal" gap={3} className=" mb-2 flex-wrap-reverse row-gap-1">
                            <Col xs={12} xl xxl={5}>
                                <SearchInput filterData={filterData} setFilterData={setFilterData} />
                            </Col>
                            <Stack direction="horizontal" gap={1} className=" ms-xl-auto">
                                <span className="me-1">Hide deltas(%):</span>
                                <Form.Switch
                                    className="form-switch-lg"
                                    id={`${moduleName}-hide-deltas`}
                                    size="m"
                                    checked={filterData.hideDeltas}
                                    onChange={e => setFilterData({ ...filterData, hideDeltas: e.target.checked })}
                                />
                            </Stack>
                            <Stack direction="horizontal" gap={1}>
                                <span className="me-1">Hide Invalid Deltas:</span>
                                <Form.Switch
                                    className="form-switch-lg"
                                    id={`${moduleName}-hide-invalid`}
                                    size="m"
                                    checked={filterData.hideInvalid}
                                    onChange={e => setFilterData({ ...filterData, hideInvalid: e.target.checked })}
                                />
                            </Stack>
                            <Stack direction="horizontal" gap={1}>
                                <span className="me-1">Spins:</span>
                                <MinMaxSpinsPicker filterData={filterData} setFilterData={setFilterData} name={moduleName} inputOnly />
                            </Stack>
                        </Stack>
                        <div className=" overflow-x-auto">
                            <MusicTestHeader
                                isCombined={isCombined}
                                burnLocked={burnLocked}
                                hideDeltas={filterData.hideDeltas}
                                radioIds={data.radio_ids}
                                sortConfig={sortConfig}
                                requestSort={requestSort}
                            />
                            <VirtualizedWindowContainer className="music-test-mw" containerRef={tableContainerRef} virtualizer={virtualizer}>
                                {virtualizer.getVirtualItems().map(virtualRow => {
                                    const item = sortedItems[virtualRow.index];
                                    if (!item) return <EmptyRow key={virtualRow.key} text="No songs" />;
                                    return (
                                        <MusicTestRow
                                            key={virtualRow.key}
                                            isCombined={isCombined}
                                            burnLocked={burnLocked}
                                            hideDeltas={filterData.hideDeltas}
                                            showDeltaSpins={showDeltaSpins}
                                            item={item}
                                            radioIds={data.radio_ids}
                                            virtualizer={virtualizer}
                                            virtualRow={virtualRow}
                                            setCatalogueSong={setCatalogueSong}
                                            setTrendSong={setTrendSong}
                                        />
                                    );
                                })}
                            </VirtualizedWindowContainer>
                        </div>
                    </>
                )}
                <MusicTestTrendModalComponent radioId={data?.radio_ids[0]} dataType={formData.demo[0]?.data_type} daypartId={formData.daypartId} />
                <SongCatalogueModalComponent />
            </ModulePage.DataContent>
        </ModulePage>
    );
}
